/**
 * Host Module Federation của cổng tenant (ADR-0096): nạp giao diện module đã cài từ điểm vào mà
 * `GET /api/v1/modules/installed` trả về.
 *
 * Hợp đồng với remote (`@foxaitechnologyjsc/module-sdk` dựng sẵn): `remoteEntry.js` dạng ESM, expose `./App` với
 * `default` là một component nhận `ModuleAppProps`. Cổng cung cấp `react`, `react-dom`, `antd`,
 * `@ant-design/icons` dạng singleton: remote dùng antd của cổng nên nhận đúng `ConfigProvider` (chủ đề
 * sáng, tối, màu nhấn, bo góc người dùng chọn). Host đặt bí danh theo `code`, nên `name` remote tự khai
 * lúc build không cần trùng.
 *
 * Điểm vào `*.mount.js` (ADR-0110) là nhánh thứ hai: ESM xuất `mount(el, props)` viết bằng framework bất kỳ,
 * nạp bằng `import()` gốc, không qua Module Federation; cổng bọc nó thành component React bằng `taoManHinhTuMount`.
 */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { version as phienBanAntd } from 'antd';
import type { ComponentType } from 'react';
import type { createInstance } from '@module-federation/runtime';

import { loaiDiemVao } from './loai-diem-vao';
import { taoManHinhTuMount } from './mount-wrapper';
import type { MountFn } from './mount-wrapper';

/** Đồng bộ với `ModuleAppProps` của `sdk/module-web/src/index.tsx`. */
export interface ModuleAppProps {
  /** Đường dẫn gốc của module trong cổng, ví dụ `/client/m/demo-sdk`. */
  basePath: string;
  /** Phần sau `basePath`, luôn mở đầu bằng `/`; khớp `path` của `menus`. */
  path: string;
  locale: 'vi' | 'en';
  getAccessToken: () => Promise<string | undefined>;
  /** Chuyển tới một đường dẫn trong module, tương đối với `basePath`. */
  navigate: (path: string) => void;
  hasPermission: (code: string) => boolean;
}

type MfInstance = ReturnType<typeof createInstance>;

let mf: MfInstance | undefined;
const entryDaDangKy = new Map<string, string>();

function chinh(phienBan: string): string {
  return `^${phienBan.split('.')[0]}.0.0`;
}

/** Runtime và antd chỉ tải khi mở màn module đầu tiên, không nằm trong bundle chính. */
async function layMf(): Promise<MfInstance> {
  if (mf) return mf;
  const { createInstance: taoThucThe } = await import('@module-federation/runtime');
  mf ??= taoThucThe({
    name: 'native_admin',
    remotes: [],
    shared: {
      react: {
        version: React.version,
        lib: () => React,
        shareConfig: { singleton: true, requiredVersion: chinh(React.version) },
      },
      'react-dom': {
        version: React.version,
        lib: () => ReactDOM,
        shareConfig: { singleton: true, requiredVersion: chinh(React.version) },
      },
      antd: {
        version: phienBanAntd,
        get: () => import('antd').then((m) => () => m),
        shareConfig: { singleton: true, requiredVersion: chinh(phienBanAntd) },
      },
      '@ant-design/icons': {
        version: __PHIEN_BAN_ANT_ICONS__,
        get: () => import('@ant-design/icons').then((m) => () => m),
        shareConfig: { singleton: true, requiredVersion: chinh(__PHIEN_BAN_ANT_ICONS__) },
      },
    },
  });
  return mf;
}

export function biDanhRemote(code: string): string {
  return `module_${code.replace(/-/g, '_')}`;
}

export class LoiHopDongRemote extends Error {}

/** Nhánh `mount` (ADR-0110): URL tuyệt đối vì `import()` gốc không hiểu đường dẫn tương đối của route tệp giao diện. */
async function napManHinhMount(entry: string): Promise<ComponentType<ModuleAppProps>> {
  const url = new URL(entry, window.location.origin).href;
  const mod = (await import(/* @vite-ignore */ url)) as { mount?: unknown; default?: unknown };
  const mount = typeof mod.mount === 'function' ? mod.mount : mod.default;
  if (typeof mount !== 'function') {
    throw new LoiHopDongRemote(`${entry} không xuất hàm mount`);
  }
  return taoManHinhTuMount(mount as MountFn);
}

/**
 * Nạp màn hình của module: `./App` của remote Module Federation, hoặc `mount` của điểm vào `*.mount.js`.
 * Cùng `code` mà đổi `entry` (vừa nâng cấp) thì đăng ký lại.
 */
export async function napManHinhModule(code: string, entry: string): Promise<ComponentType<ModuleAppProps>> {
  if (loaiDiemVao(entry) === 'mount') return napManHinhMount(entry);
  const ten = biDanhRemote(code);
  const thucThe = await layMf();
  const daCo = entryDaDangKy.get(ten);
  if (daCo !== entry) {
    // Đường dẫn tương đối (giao diện trong gói, phục vụ ở /api/v1/module-assets/...) đổi thành tuyệt đối:
    // import() của runtime cần URL đầy đủ để nạp tiếp chunk theo đường dẫn tương đối của remoteEntry.js.
    const url = new URL(entry, window.location.origin).href;
    thucThe.registerRemotes([{ name: ten, entry: url, type: 'module' }], { force: daCo !== undefined });
    entryDaDangKy.set(ten, entry);
  }
  const mod = await thucThe.loadRemote<{ default?: unknown }>(`${ten}/App`);
  if (!mod || typeof mod.default !== 'function') {
    throw new LoiHopDongRemote(`${ten}/App không có default export là component`);
  }
  return mod.default as ComponentType<ModuleAppProps>;
}
